import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Heart, ArrowRight, ArrowLeft, Sparkles, Check, MessageCircle, RotateCcw } from "lucide-react";
import { useLang } from "@/lib/i18n";

const WA_NUMBER = "5217712840787";

const computeResult = (answers, t) => {
  const score = (answers.evitar?.v || 0) + (answers.emocion?.v || 0) + (answers.impacto?.v || 0);
  const tone = score <= 2 ? t.quiz.tones.low : score <= 5 ? t.quiz.tones.mid : t.quiz.tones.high;
  const areas = answers.areas?.v || [];
  const treatments = [...new Set(areas.filter((a) => t.quiz.treatments[a]).map((a) => t.quiz.treatments[a]))];
  return { tone, areas, treatments, score };
};

export default function SelfEsteemQuiz({ open, onOpenChange }) {
  const { t } = useLang();
  const QUESTIONS = t.quiz.questions;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const total = QUESTIONS.length;
  const isResult = step === total;
  const q = !isResult ? QUESTIONS[step] : null;
  const progress = Math.round(((isResult ? total : step) / total) * 100);

  const setAnswer = (val) => setAnswers((a) => ({ ...a, [q.id]: { v: val } }));

  const toggleMulti = (val) => {
    const current = answers[q.id]?.v || [];
    const next = current.includes(val) ? current.filter((x) => x !== val) : [...current, val];
    setAnswer(next);
  };

  const currentAns = q ? answers[q.id]?.v : undefined;
  const canNext = q?.type === "multi"
    ? Array.isArray(currentAns) && currentAns.length > 0
    : currentAns !== undefined && currentAns !== null;

  const reset = () => { setStep(0); setAnswers({}); };

  const result = isResult ? computeResult(answers, t) : null;

  useEffect(() => {
    if (isResult && result) {
      api.post("/quiz/submit", {
        answers,
        areas: result.areas,
        treatments: result.treatments,
        score: result.score,
      }).catch(() => { /* noop */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResult]);

  const buildWa = () => {
    const areasLabels = (result?.areas || [])
      .map((a) => QUESTIONS[3].options.find((o) => o.v === a)?.label)
      .filter(Boolean)
      .join(", ");
    const treatments = (result?.treatments || []).join(" + ");
    const msg = t.quiz.whatsappMsg({ treatments, areas: areasLabels });
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent translate="no" className="notranslate max-w-2xl p-0 overflow-hidden bg-white border-[#E6E2DC]" data-testid="quiz-dialog">
        <div className="bg-[#1A1C1A] text-[#D9CDBC] px-8 py-6">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A69076]">
            <Heart className="w-3.5 h-3.5" /> {t.quiz.header}
          </div>
          <h3 className="font-display text-2xl text-[#FAFAFA] mt-2">
            {isResult ? t.quiz.resultTitle : t.quiz.questionLabel(step + 1, total)}
          </h3>
          <Progress value={progress} className="mt-4 h-1 bg-[#2a2b29]" />
        </div>

        <div className="p-8 max-h-[60vh] overflow-y-auto">
          {!isResult && (
            <div data-testid={`quiz-step-${step}`}>
              <p className="font-display text-2xl text-[#1A1C1A] leading-snug mb-6">{q.title}</p>

              {q.type === "single" && (
                <div className="space-y-2.5">
                  {q.options.map((o) => {
                    const selected = answers[q.id]?.v === o.v;
                    return (
                      <button
                        key={String(o.v)}
                        onClick={() => setAnswer(o.v)}
                        data-testid={`quiz-opt-${q.id}-${o.v}`}
                        className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center gap-3 ${selected ? "border-[#A69076] bg-[#F4F0EB]" : "border-[#E6E2DC] hover:border-[#D9CDBC] bg-white"}`}
                      >
                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-[#A69076] bg-[#A69076]" : "border-[#D9CDBC]"}`}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="text-sm text-[#1A1C1A]">{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {q.type === "multi" && (
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {q.options.map((o) => {
                    const current = answers[q.id]?.v || [];
                    const selected = current.includes(o.v);
                    return (
                      <button
                        key={o.v}
                        onClick={() => toggleMulti(o.v)}
                        data-testid={`quiz-opt-${q.id}-${o.v}`}
                        className={`text-left px-4 py-3.5 rounded-xl border transition-all flex items-center gap-3 ${selected ? "border-[#A69076] bg-[#F4F0EB]" : "border-[#E6E2DC] hover:border-[#D9CDBC] bg-white"}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected ? "border-[#A69076] bg-[#A69076]" : "border-[#D9CDBC]"}`}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="text-xs text-[#1A1C1A] leading-tight">{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isResult && (
            <div data-testid="quiz-result" translate="no" className="notranslate">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-2">{t.quiz.empathyLabel}</div>
              <h4 className="font-display text-3xl text-[#1A1C1A] leading-tight"><span>{result.tone.title}</span></h4>
              <p className="mt-4 text-[15px] text-[#5C5E5C] leading-relaxed"><span>{result.tone.body}</span></p>

              <div className="mt-7 bg-[#F4F0EB] rounded-2xl p-6 border border-[#E6E2DC]">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-3">
                  <Sparkles className="w-3.5 h-3.5" /> {t.quiz.preDxLabel}
                </div>
                {result.areas.length === 0 || result.areas.includes("asesoria") ? (
                  <p className="text-sm text-[#1A1C1A]"><span>{t.quiz.onlyAdvice.pre}</span><strong>{t.quiz.onlyAdvice.strong}</strong><span>{t.quiz.onlyAdvice.post}</span></p>
                ) : (
                  <div>
                    <p className="text-sm text-[#1A1C1A] mb-3"><span>{t.quiz.preDxIntro}</span></p>
                    <ul className="space-y-2">
                      {result.treatments.map((tr, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#1A1C1A]">
                          <Check className="w-4 h-4 text-[#7A9180] mt-0.5 shrink-0" /> <span>{tr}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-[#5C5E5C] mt-4 italic"><span>{t.quiz.preDxDisclaimer}</span></p>
                  </div>
                )}
              </div>

              <div className="mt-6 bg-gradient-to-br from-[#1A1C1A] to-[#2a2b29] text-[#FAFAFA] rounded-2xl p-6 border border-[#1A1C1A]">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> {t.quiz.rewardLabel}
                </div>
                <h5 className="font-display text-2xl">{t.quiz.discount}</h5>
                <p className="text-sm text-[#D9CDBC] mt-1">{t.quiz.discountBody}</p>
                <div className="mt-4 inline-flex items-center gap-2 bg-[#A69076] text-[#1A1C1A] rounded-full px-4 py-1.5 text-xs font-medium tracking-widest">
                  {t.quiz.code}
                </div>
              </div>

              <a href={buildWa()} target="_blank" rel="noreferrer" data-testid="quiz-whatsapp-cta">
                <Button className="mt-6 w-full rounded-full bg-[#7A9180] hover:bg-[#5d7a66] text-white h-14 text-sm uppercase tracking-[0.2em]">
                  <MessageCircle className="w-5 h-5 mr-2" /> {t.quiz.whatsappBtn}
                </Button>
              </a>
            </div>
          )}
        </div>

        <div className="px-8 py-5 border-t border-[#E6E2DC] bg-[#FAFAFA] flex items-center justify-between">
          {!isResult ? (
            <>
              <Button
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep((s) => s - 1)}
                className="text-[#5C5E5C] hover:text-[#1A1C1A]"
                data-testid="quiz-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> {t.quiz.back}
              </Button>
              <Button
                disabled={!canNext}
                onClick={() => setStep((s) => s + 1)}
                className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-11 px-7 text-xs uppercase tracking-[0.2em]"
                data-testid="quiz-next"
              >
                {step === total - 1 ? t.quiz.finish : t.quiz.next} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={reset} className="text-[#5C5E5C]" data-testid="quiz-restart">
                <RotateCcw className="w-4 h-4 mr-1" /> {t.quiz.restart}
              </Button>
              <span className="text-xs text-[#5C5E5C]">{t.quiz.couponNote}</span>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QuizTrigger({ onClick }) {
  const { t } = useLang();
  return (
    <button onClick={onClick} className="block w-full text-left group" data-testid="quiz-open">
      <div className="bg-gradient-to-br from-[#F4F0EB] to-[#E6E2DC] rounded-3xl p-8 lg:p-12 border border-[#E6E2DC] hover:border-[#A69076] transition-all overflow-hidden relative">
        <div className="absolute -top-10 -right-10 w-48 h-48 bg-[#A69076]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-4">
              <Heart className="w-3.5 h-3.5" /> {t.quizCta.tag}
            </div>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1C1A]">
              {t.quizCta.title1}<br />{t.quizCta.title2}
            </h3>
            <p className="mt-4 text-[15px] text-[#5C5E5C] max-w-xl leading-relaxed">
              {t.quizCta.body1} <strong className="text-[#1A1C1A]">{t.quizCta.bodyStrong1}</strong>{t.quizCta.body2} <strong className="text-[#1A1C1A]">{t.quizCta.bodyStrong2}</strong> {t.quizCta.body3}
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#1A1C1A] text-white px-7 h-12 text-xs uppercase tracking-[0.2em] group-hover:bg-[#A69076] transition-colors">
              {t.quizCta.cta} <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
